interface TimeFormatOptions {
  locale?: string;
  timeZone?: string;
}

export class TimeFormatter {
  private locale: string;
  private timeZone: string;

  constructor(options: TimeFormatOptions = {}) {
    this.locale = options.locale || "en-US";
    this.timeZone =
      options.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  formatPostTime(createdAt: string | Date): string {
    const now = new Date();
    const postDate = new Date(createdAt);
    const diffInMs = now.getTime() - postDate.getTime();
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    // 0-59 seconds → just now
    if (diffInSeconds < 60) {
      return "just now";
    }

    // 1-59 minutes → 3m ago
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    }

    // 1-23 hours → 4h ago
    if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    }

    // Yesterday (00:00–23:59 local time yesterday) → yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (this.isSameDay(postDate, yesterday)) {
      return "yesterday";
    }

    // 2-6 days → Tue / Fri (weekday only)
    if (diffInDays >= 2 && diffInDays <= 6) {
      return postDate.toLocaleDateString(this.locale, { weekday: "short" });
    }

    // 7-29 days → Aug 12
    if (diffInDays >= 7 && diffInDays <= 29) {
      return postDate.toLocaleDateString(this.locale, {
        month: "short",
        day: "numeric",
      });
    }

    // 30-364 days → Aug 12 (keep same format - don't add year yet)
    if (diffInDays >= 30 && diffInDays <= 364) {
      return postDate.toLocaleDateString(this.locale, {
        month: "short",
        day: "numeric",
      });
    }

    // ≥ 1 year → Aug 12, 2023
    return postDate.toLocaleDateString(this.locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  formatFullTime(createdAt: string | Date): string {
    const postDate = new Date(createdAt);
    return postDate.toLocaleDateString(this.locale, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  }

  /** 12-hour time with AM/PM for U.S. notification display */
  private format12h(d: Date): string {
    const h = d.getHours();
    const m = d.getMinutes();
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    const minStr = m < 10 ? `0${m}` : `${m}`;
    return `${h12}:${minStr} ${ampm}`;
  }

  /**
   * Notification display time: relative for recent, then "Yesterday • 12:31 PM", "Feb 14 • 3:45 AM".
   * U.S.-friendly: month first, 12-hour AM/PM.
   */
  formatNotificationTime(createdAt: string | Date): string {
    const now = new Date();
    const date = new Date(createdAt);
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffSec < 60) return "Just now";
    if (diffMin < 60) return `${diffMin} min ago`;
    if (diffHours < 24) {
      return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
    }

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (this.isSameDay(date, yesterday)) {
      return `Yesterday • ${this.format12h(date)}`;
    }

    const monthDay = date.toLocaleDateString(this.locale, {
      month: "short",
      day: "numeric",
    });
    return `${monthDay} • ${this.format12h(date)}`;
  }

  /**
   * Full date/time for notification tooltip: "Feb 14, 2026 • 3:45 AM".
   */
  formatNotificationTimeFull(createdAt: string | Date): string {
    const date = new Date(createdAt);
    const dateStr = date.toLocaleDateString(this.locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${dateStr} • ${this.format12h(date)}`;
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }
}

export const timeFormatter = new TimeFormatter();

export const formatPostTime = (createdAt: string | Date): string => {
  return timeFormatter.formatPostTime(createdAt);
};

export const formatFullTime = (createdAt: string | Date): string => {
  return timeFormatter.formatFullTime(createdAt);
};

export const formatPostDate = (createdAt: string | Date): string => {
  const postDate = new Date(createdAt);
  return postDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

export const formatNotificationTime = (createdAt: string | Date): string => {
  return timeFormatter.formatNotificationTime(createdAt);
};

export const formatNotificationTimeFull = (createdAt: string | Date): string => {
  return timeFormatter.formatNotificationTimeFull(createdAt);
};
